// FILE: src/admin/pages/support/tabs/SupportBookingsTab.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  searchAdminSupportBookingsForUser,
  getAdminSupportBookingDetail,
} from "../../../../api/adminSupportApi";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

/* --------------------- utils --------------------- */
function safeStr(x) {
  return x == null ? "" : String(x).trim();
}
function up(x) {
  return safeStr(x).toUpperCase();
}

function timeAgo(date) {
  const ms = Date.now() - date.getTime();
  if (!Number.isFinite(ms)) return "";
  const sec = Math.floor(ms / 1000);
  if (sec < 30) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mon = Math.floor(day / 30);
  if (mon < 12) return `${mon} month${mon === 1 ? "" : "s"} ago`;
  const yr = Math.floor(mon / 12);
  return `${yr} year${yr === 1 ? "" : "s"} ago`;
}

function fmtDT(x) {
  if (!x) return { pretty: "—", rel: "", raw: "" };
  const d = new Date(x);
  if (Number.isNaN(d.getTime())) return { pretty: String(x), rel: "", raw: String(x) };
  const pretty = new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
  return { pretty, rel: timeAgo(d), raw: d.toISOString() };
}

function copyText(x) {
  const v = String(x || "");
  if (!v) return;
  try {
    navigator.clipboard?.writeText(v);
  } catch {}
}

function fmtMoney(amount) {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
}

function fmtNum(x, digits = 2) {
  if (x == null || x === "") return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return String(x);
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: digits }).format(n);
}

function fmtLatLng(lat, lng) {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return "—";
  return `${a.toFixed(6)}, ${b.toFixed(6)}`;
}

/** Backend uses an envelope: { ok, message, ts, data }. Keep best-effort compatibility. */
function unwrapApi(x) {
  if (x && typeof x === "object" && "ok" in x && "data" in x) return x.data;
  return x;
}

function extractBookingAndAudit(detailEnvelope) {
  const detail = unwrapApi(detailEnvelope);

  const booking =
    detail?.detail?.booking ||
    detail?.booking ||
    detail?.data?.detail?.booking ||
    detail?.data?.booking ||
    null;

  const auditPage = detail?.audit || detail?.detail?.audit || detail?.data?.audit || null;

  const auditItems = Array.isArray(auditPage?.items)
    ? auditPage.items
    : Array.isArray(auditPage)
    ? auditPage
    : [];

  const auditMeta = auditPage?.page || null;

  return { booking, auditItems, auditMeta, raw: detail };
}

/**
 * New DTOs:
 * - list row: row.latestRide { rideId, rideStatus, ... }
 * - detail: detail.detail.ride { rideId, status, ... }
 */
function extractRideBestEffort({ detailEnvelope, row } = {}) {
  const detail = unwrapApi(detailEnvelope);

  const fromDetail = detail?.detail?.ride || detail?.ride || null;
  const fromRow = row?.latestRide || row?.ride || row?.trip || null;

  const rideId =
    fromDetail?.rideId ??
    fromDetail?.id ??
    fromRow?.rideId ??
    fromRow?.id ??
    row?.rideId ??
    row?.ride_id ??
    null;

  const rideStatus =
    fromDetail?.status ??
    fromDetail?.rideStatus ??
    fromRow?.rideStatus ??
    fromRow?.status ??
    row?.rideStatus ??
    row?.ride_status ??
    null;

  const merged =
    fromDetail || fromRow
      ? { ...(fromRow || {}), ...(fromDetail || {}), rideId, rideStatus }
      : rideId || rideStatus
      ? { rideId, rideStatus }
      : null;

  return merged;
}

/* =========================================================
   ✅ ROUTE + SCHEDULE EXTRACTION (Best-effort)
   IMPORTANT: Route/Schedule should NOT appear in main table.
   They are used for:
   - drawer detail (single source of truth for display)
   - search tokens (still allowed)
   - CSV export (still allowed)
   ========================================================= */
function extractRouteScheduleBestEffort({ detailEnvelope, row, booking } = {}) {
  const detail = unwrapApi(detailEnvelope);

  const route = detail?.detail?.route || detail?.route || null;
  const schedule = detail?.detail?.schedule || detail?.schedule || null;

  const src = booking || detail?.detail?.booking || detail?.booking || row || {};

  const pickupText =
    safeStr(route?.pickupText) ||
    safeStr(src?.pickupText) ||
    safeStr(src?.pickup) ||
    safeStr(src?.pickup_address) ||
    "";

  const destinationText =
    safeStr(route?.destinationText) ||
    safeStr(src?.finalDestination) ||
    safeStr(src?.destination) ||
    safeStr(src?.dropText) ||
    "";

  const stops = Array.isArray(route?.stops)
    ? route.stops.filter(Boolean).map((s) => safeStr(s)).filter(Boolean)
    : Array.isArray(src?.stops)
    ? src.stops.filter(Boolean).map((s) => safeStr(s)).filter(Boolean)
    : [];

  const pickupAt = schedule?.pickupAt ?? src?.pickupAt ?? src?.scheduledAt ?? null;
  const dropAt = schedule?.dropAt ?? src?.dropAt ?? null;

  return { pickupText, destinationText, stops, pickupAt, dropAt };
}

/* =========================================================
   ✅ PAYMENT EXTRACTION (Best-effort)
   ✅ normalize to SAME labels as Requests table:
      PAID / PENDING / FAILED / REFUNDED / PARTIAL_REFUND
   ========================================================= */
function normalizePaymentStatus(raw) {
  const s = up(raw);
  if (!s) return "";

  if (s === "PAID" || s === "SUCCESS" || s === "CAPTURED") return "PAID";
  if (s === "FAILED" || s === "FAIL") return "FAILED";
  if (s === "PENDING" || s === "CREATED") return "PENDING";
  if (s === "REFUNDED" || s === "FULLY_REFUNDED") return "REFUNDED";
  if (
    s === "PARTIAL_REFUND" ||
    s === "PARTIALLY_REFUNDED" ||
    s === "PARTIAL_REFUNDED" ||
    s === "PARTIAL"
  )
    return "PARTIAL_REFUND";

  return s;
}

function extractPaymentBestEffort({ detailEnvelope, row } = {}) {
  const detail = unwrapApi(detailEnvelope);

  const fromDetail =
    detail?.detail?.payment ||
    detail?.payment ||
    detail?.detail?.paymentInfo ||
    detail?.paymentInfo ||
    null;

  const fromRow =
    row?.payment ||
    row?.latestPayment ||
    row?.paymentInfo ||
    row?.paymentSummary ||
    row?.paymentDetail ||
    null;

  const paymentId =
    fromDetail?.paymentId ??
    fromDetail?.id ??
    fromRow?.paymentId ??
    fromRow?.id ??
    row?.paymentId ??
    row?.payment_id ??
    row?.payment?.id ??
    null;

  const paymentStatusRaw =
    fromDetail?.status ??
    fromDetail?.paymentStatus ??
    fromRow?.paymentStatus ??
    fromRow?.status ??
    row?.paymentStatus ??
    row?.payment_status ??
    null;

  const razorpayPaymentId =
    fromDetail?.razorpayPaymentId ??
    fromDetail?.rpPaymentId ??
    fromRow?.razorpayPaymentId ??
    fromRow?.rpPaymentId ??
    row?.razorpayPaymentId ??
    row?.rpPaymentId ??
    null;

  const razorpayOrderId =
    fromDetail?.razorpayOrderId ??
    fromDetail?.rpOrderId ??
    fromRow?.razorpayOrderId ??
    fromRow?.rpOrderId ??
    row?.razorpayOrderId ??
    row?.rpOrderId ??
    null;

  const razorpayRefundId =
    fromDetail?.razorpayRefundId ??
    fromDetail?.rpRefundId ??
    fromRow?.razorpayRefundId ??
    fromRow?.rpRefundId ??
    row?.razorpayRefundId ??
    row?.rpRefundId ??
    null;

  const merged =
    fromDetail || fromRow || paymentId || paymentStatusRaw
      ? {
          ...(fromRow || {}),
          ...(fromDetail || {}),
          paymentId,
          paymentStatus: normalizePaymentStatus(paymentStatusRaw),
          razorpayPaymentId,
          razorpayOrderId,
          razorpayRefundId,
        }
      : null;

  return merged;
}

/* =========================================================
   ✅ FARE BREAKDOWN
   Prefer backend-computed booking pricing so admin sees one source of truth.
   ========================================================= */
function computeFareBreakdown({ booking, row, payment } = {}) {
  const src = booking || row || {};
  const pricing = src?.fare || src?.pricing || {};
  const pay = payment || src?.payment || {};

  const gst = pricing?.gst != null ? Number(pricing.gst) : null;
  const platformFee =
    pricing?.platformFee != null
      ? Number(pricing.platformFee)
      : pricing?.apiCharges != null
      ? Number(pricing.apiCharges)
      : null;
  const walletDeduction =
    pricing?.walletDeduction != null
      ? Number(pricing.walletDeduction)
      : Number.isFinite(platformFee) && Number.isFinite(gst)
      ? platformFee + gst
      : null;
  const netAmountToDriver =
    pricing?.netAmountToDriver != null
      ? Number(pricing.netAmountToDriver)
      : pricing?.ownerNet != null
      ? Number(pricing.ownerNet)
      : null;

  let total =
    pricing?.totalFare != null ? Number(pricing.totalFare) :
    pricing?.fare != null ? Number(pricing.fare) :
    pay?.amount != null ? Number(pay.amount) :
    pay?.paidAmount != null ? Number(pay.paidAmount) :
    null;

  if (!Number.isFinite(total)) {
    const base = pricing?.basePrice != null ? Number(pricing.basePrice) : 0;
    const fuel = pricing?.fuelCost != null ? Number(pricing.fuelCost) : 0;
    const toll = pricing?.toll != null ? Number(pricing.toll) : 0;
    const g = Number.isFinite(gst) ? gst : 0;
    const s = base + fuel + toll + g;
    total = Number.isFinite(s) && s > 0 ? s : null;
  }

  const gstFinite = Number.isFinite(gst) ? gst : null;
  const totalFinite = Number.isFinite(total) ? total : null;

  const excl =
    totalFinite != null && gstFinite != null ? totalFinite - gstFinite : totalFinite;

  return {
    total: totalFinite,
    exclGst: excl != null && Number.isFinite(excl) ? excl : null,
    gst: gstFinite,
    platformFee: Number.isFinite(platformFee) ? platformFee : null,
    walletDeduction: Number.isFinite(walletDeduction) ? walletDeduction : null,
    netAmountToDriver: Number.isFinite(netAmountToDriver) ? netAmountToDriver : null,
  };
}

/* =========================================================
   ✅ REAL STATUS NORMALIZATION (no imaginary states)
   ========================================================= */

const BOOKING_STATUS_VALUES = ["ALL", "BOOKED", "IN_PROGRESS", "COMPLETED", "CANCELLED"];
const RIDE_STATUS_VALUES = ["ALL", "NO_RIDE", "ARRIVED_AT_PICKUP", "IN_PROGRESS", "ENDED"];

function normalizeBookingStatus(raw) {
  const s = up(raw);
  if (!s) return "";
  if (s === "DONE" || s === "FINISHED" || s === "COMPLETE") return "COMPLETED";
  return s;
}

function normalizeRideStatus(raw) {
  const s = up(raw);
  if (!s) return "";
  if (s === "COMPLETED" || s === "FINISHED") return "ENDED";
  if (s === "END") return "ENDED";
  return s;
}

function bookingStatusFromRow(r) {
  return normalizeBookingStatus(
    r?.status ?? r?.bookingStatus ?? r?.bookingStatusRaw ?? r?.booking_status ?? r?.bookingStatusEffective
  );
}

function rideFromRow(r) {
  return extractRideBestEffort({ row: r }) || null;
}

function rideStatusFromRow(r) {
  const ride = rideFromRow(r);
  return normalizeRideStatus(ride?.rideStatus ?? ride?.status ?? r?.rideStatus ?? r?.ride_status);
}

function paymentFromRow(r) {
  return extractPaymentBestEffort({ row: r }) || null;
}

/* =========================================================
   ✅ SEARCH (robust)
   NOTE: Route/schedule remain searchable even though hidden in table.
   ========================================================= */

function normForSearch(x) {
  return up(x).replace(/[^A-Z0-9_]/g, "");
}

function rowMatchesQuery(r, q) {
  const qq = safeStr(q);
  if (!qq) return true;

  const needleUpper = up(qq);
  const needleNorm = normForSearch(qq);

  const bookingId = r?.bookingId ?? r?.id ?? r?.booking_id ?? "";
  const ownerId = r?.owner?.ownerId ?? r?.ownerId ?? r?.owner_id ?? "";
  const driverId =
    r?.assignedDriver?.driverId ?? r?.driver?.driverId ?? r?.driverId ?? r?.driver_id ?? "";

  const vehicleLabel =
    r?.vehicle?.registrationNumber ||
    r?.vehicle?.regNo ||
    r?.vehicle?.vehicleNo ||
    r?.vehicleRegNo ||
    r?.vehicle_no ||
    "";

  // Keep route searchable (but hidden in main table UI)
  const rs = extractRouteScheduleBestEffort({ row: r });
  const pickupText = rs?.pickupText ?? r?.pickupText ?? r?.pickup ?? r?.pickup_address ?? "";
  const destination = rs?.destinationText ?? r?.finalDestination ?? r?.destination ?? r?.dropText ?? "";
  const stops = Array.isArray(rs?.stops) ? rs.stops.join(" | ") : Array.isArray(r?.stops) ? r.stops.join(" | ") : "";

  const bStatus = bookingStatusFromRow(r) || "";

  const ride = rideFromRow(r);
  const rideId = ride?.rideId ?? "";
  const rideStatus = rideStatusFromRow(r) || "";
  const rideStatusCell = rideId ? rideStatus : "NO_RIDE";

  const pay = paymentFromRow(r);
  const paymentId = pay?.paymentId ?? "";
  const paymentStatus = pay?.paymentStatus ?? "";
  const rpPayId = pay?.razorpayPaymentId ?? "";
  const rpOrderId = pay?.razorpayOrderId ?? "";
  const rpRefundId = pay?.razorpayRefundId ?? "";

  const pickupAt = fmtDT(rs?.pickupAt || r?.pickupAt || r?.scheduledAt);
  const dropAt = fmtDT(rs?.dropAt || r?.dropAt);
  const created = fmtDT(r?.createdAt);

  const fare =
    r?.pricing?.fare ??
    r?.pricing?.basePrice ??
    r?.fare ??
    r?.amount ??
    r?.payment?.amount ??
    null;

  const tokens = [
    bookingId,
    `#${bookingId}`,
    ownerId,
    `#${ownerId}`,
    driverId,
    `#${driverId}`,
    vehicleLabel,
    pickupText,
    destination,
    stops,
    bStatus,
    rideId,
    `#${rideId}`,
    rideStatus,
    rideStatusCell,
    paymentId,
    `#${paymentId}`,
    paymentStatus,
    rpPayId,
    rpOrderId,
    rpRefundId,
    pickupAt.pretty,
    pickupAt.raw,
    dropAt.pretty,
    dropAt.raw,
    created.pretty,
    created.raw,
    fare != null ? String(fare) : "",
    fare != null ? fmtMoney(fare) : "",
  ].filter(Boolean);

  const hayUpper = tokens.map((x) => up(x)).join(" | ");
  if (hayUpper.includes(needleUpper)) return true;

  if (!needleNorm) return false;
  const hayNorm = normForSearch(hayUpper);
  return hayNorm.includes(needleNorm);
}

/* --------------------- pagination helpers --------------------- */
function buildPageButtons({ page, totalPages, maxButtons = 7 }) {
  const tp = Math.max(1, Number(totalPages || 1));
  const p = Math.min(Math.max(0, Number(page || 0)), tp - 1);
  const max = Math.max(5, maxButtons);

  if (tp <= max) return Array.from({ length: tp }, (_, i) => i);

  const out = [];
  const first = 0;
  const last = tp - 1;

  const windowSize = max - 2;
  const half = Math.floor(windowSize / 2);

  let start = p - half;
  let end = p + half;

  if (start < 1) {
    start = 1;
    end = start + windowSize - 1;
  }
  if (end > last - 1) {
    end = last - 1;
    start = end - windowSize + 1;
  }

  out.push(first);
  if (start > 1) out.push("…");
  for (let i = start; i <= end; i++) out.push(i);
  if (end < last - 1) out.push("…");
  out.push(last);

  return out;
}

/* --------------------- UI atoms --------------------- */
function Tag({ label, tone = "neutral", className = "" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <span
      className={classNames(
        "inline-flex items-center border px-2 py-1 text-[11px] font-bold uppercase tracking-wider leading-none",
        "rounded-md",
        cls,
        className
      )}
    >
      {label}
    </span>
  );
}

function ScopeChip({ label, subtitle = "User" }) {
  return (
    <div className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-900">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{subtitle}</span>
      <span className="tabular-nums">{label}</span>
    </div>
  );
}

function StatusTag({ status }) {
  const s = up(status);
  const tone =
    s === "COMPLETED" || s.includes("FULFIL") || s === "BOOKED"
      ? "good"
      : s.includes("PENDING") || s.includes("IN_PROGRESS") || s.includes("ASSIGN") || s.includes("REASSIGN")
      ? "warn"
      : s.includes("CANCEL") || s.includes("FAIL") || s.includes("DECLIN")
      ? "bad"
      : "neutral";

  return <Tag label={s || "—"} tone={tone} />;
}

function RideStatusTag({ status }) {
  const s = up(status);
  if (!s) return <Tag label="—" tone="neutral" />;
  const tone =
    s === "ENDED" || s === "COMPLETED" || s === "FINISHED"
      ? "good"
      : s.includes("ARRIVED") || s.includes("START") || s.includes("RUN") || s.includes("ONGO") || s.includes("IN_PROGRESS")
      ? "warn"
      : s.includes("CANCEL") || s.includes("FAIL")
      ? "bad"
      : "neutral";
  return <Tag label={s} tone={tone} />;
}

function PaymentStatusTag({ status }) {
  const s = up(status);
  if (!s) return <Tag label="—" tone="neutral" />;

  const tone =
    s === "PAID"
      ? "good"
      : s.includes("PENDING")
      ? "warn"
      : s.includes("FAIL") || s.includes("REFUND")
      ? "bad"
      : "neutral";

  return <Tag label={s} tone={tone} />;
}

function IconRefresh({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M20 12a8 8 0 1 1-2.34-5.66M20 4v6h-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSearch({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M10.5 18.5a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z" stroke="currentColor" strokeWidth="2" />
      <path d="M16.5 16.5 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconX({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconChevronRight({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconChevronDown({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconDownload({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M12 3v10m0 0 4-4m-4 4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M5 17v3h14v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Section({ title, subtitle, right, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
          {subtitle ? <div className="mt-1 text-[13px] font-medium text-slate-500">{subtitle}</div> : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function KV({ k, v, hint, action }) {
  return (
    <div className="grid grid-cols-12 gap-3 py-3">
      <div className="col-span-12 md:col-span-4 text-[12px] font-bold uppercase tracking-wider text-slate-500">{k}</div>
      <div className="col-span-12 md:col-span-8 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="break-words text-sm font-semibold text-slate-900">{v ?? "—"}</div>
          {hint ? <div className="mt-1 text-[12px] font-medium text-slate-500">{hint}</div> : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </div>
  );
}

function Button({ children, onClick, disabled, tone = "soft", className = "", title, type = "button" }) {
  const cls =
    tone === "danger"
      ? "border-rose-700 bg-rose-700 text-white hover:opacity-95"
      : tone === "dark"
      ? "border-slate-900 bg-slate-900 text-white hover:opacity-95"
      : tone === "ghost"
      ? "border-transparent bg-transparent text-slate-700 hover:bg-slate-50"
      : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50";

  return (
    <button
      type={type}
      title={title}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={classNames(
        "inline-flex h-9 items-center justify-center gap-2 border px-3 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed",
        "rounded-md",
        cls,
        className
      )}
    >
      {children}
    </button>
  );
}

function PageButton({ active, disabled, label, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={classNames(
        "inline-flex h-9 min-w-[36px] items-center justify-center rounded-md border px-3 text-[13px] font-extrabold tabular-nums",
        disabled ? "cursor-not-allowed opacity-60" : "",
        active
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
      )}
    >
      {label}
    </button>
  );
}

function SelectField({ value, onChange, children, className = "", title }) {
  return (
    <div className={classNames("relative min-w-0", className)} title={title}>
      <select
        value={value}
        onChange={onChange}
        className={classNames(
          "h-10 w-full appearance-none border bg-white pl-3 pr-10 text-sm font-semibold text-slate-900 outline-none",
          "rounded-md border-slate-200 hover:bg-slate-50 focus:border-slate-300",
          "truncate"
        )}
      >
        {children}
      </select>
      <IconChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
    </div>
  );
}

/* --------------------- list helpers --------------------- */
function extractListItems(api) {
  const x = unwrapApi(api);
  const items = Array.isArray(x?.items) ? x.items : Array.isArray(x?.content) ? x.content : [];
  const page = x?.page || null;
  const hasNext =
    typeof page?.hasNext === "boolean"
      ? page.hasNext
      : typeof x?.hasNext === "boolean"
      ? x.hasNext
      : items.length > 0;
  const totalPages = Number(page?.totalPages ?? x?.totalPages ?? 0) || 0;

  return { items, page, hasNext, totalPages };
}

function dedupeByBookingId(items) {
  const seen = new Set();
  const out = [];
  for (const r of items || []) {
    const id = r?.bookingId ?? r?.id ?? r?.booking_id;
    const key = id != null ? `B:${String(id)}` : `R:${JSON.stringify(r)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

/* =========================================================
   ✅ CSV EXPORT
   NOTE: CSV keeps route & schedule fields even though main table is clean.
   ========================================================= */
function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function rowsToCsv(rows, { entityId, bookingStatus, rideStatus, q }) {
  const headers = [
    "bookingId",
    "bookingStatus",
    "rideId",
    "rideStatus",
    "vehicle",
    "ownerId",
    "driverId",
    "pickupAt",
    "dropAt",
    "pickupText",
    "destinationText",
    "stops",
    "fareTotalInclGst",
    "gst",
    "fareExclGst",
    "paymentId",
    "paymentStatus",
    "razorpayOrderId",
    "razorpayPaymentId",
    "razorpayRefundId",
    "query",
    "filters",
  ];

  const filterStr = `bookingStatus=${bookingStatus};rideStatus=${rideStatus};userId=${entityId}`;
  const lines = [headers.join(",")];

  for (const r of rows || []) {
    const id = r?.bookingId ?? r?.id ?? r?.booking_id ?? "";
    const bStatus = bookingStatusFromRow(r) || "";
    const ride = rideFromRow(r);
    const rideId = ride?.rideId ?? "";
    const rStatus = rideId ? (rideStatusFromRow(r) || "") : "NO_RIDE";

    const ownerId = r?.owner?.ownerId ?? r?.ownerId ?? r?.owner_id ?? "";
    const driverId =
      r?.assignedDriver?.driverId ?? r?.driver?.driverId ?? r?.driverId ?? r?.driver_id ?? "";

    const vehicleLabel =
      r?.vehicle?.registrationNumber ||
      r?.vehicle?.regNo ||
      r?.vehicle?.vehicleNo ||
      r?.vehicleRegNo ||
      (r?.vehicle?.vehicleId ? `V-${r.vehicle.vehicleId}` : "");

    const rs = extractRouteScheduleBestEffort({ row: r });
    const pickupAt = fmtDT(rs.pickupAt).pretty;
    const dropAt = fmtDT(rs.dropAt).pretty;

    const fare = computeFareBreakdown({ row: r, payment: paymentFromRow(r) });

    const pay = paymentFromRow(r);
    const paymentId = pay?.paymentId ?? "";
    const paymentStatus = pay?.paymentStatus ?? "";
    const rpOrderId = pay?.razorpayOrderId ?? "";
    const rpPayId = pay?.razorpayPaymentId ?? "";
    const rpRefundId = pay?.razorpayRefundId ?? "";

    const values = [
      id,
      bStatus,
      rideId,
      rStatus,
      vehicleLabel,
      ownerId,
      driverId,
      pickupAt,
      dropAt,
      rs.pickupText || "",
      rs.destinationText || "",
      (rs.stops || []).join(" | "),
      fare.total != null ? String(fare.total) : "",
      fare.gst != null ? String(fare.gst) : "",
      fare.exclGst != null ? String(fare.exclGst) : "",
      paymentId,
      paymentStatus,
      rpOrderId,
      rpPayId,
      rpRefundId,
      q || "",
      filterStr,
    ].map(csvEscape);

    lines.push(values.join(","));
  }

  return lines.join("\n");
}

function downloadCsv(text, filename) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* --------------------- main --------------------- */
export default function SupportBookingsTab() {
  const { entityType = "users", entityId } = useParams();
  const enabled = entityType === "users";

  // Controls
  const [q, setQ] = useState("");
  const [bookingStatus, setBookingStatus] = useState("ALL"); // server filter
  const [rideStatus, setRideStatus] = useState("ALL"); // client filter
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  // Data (normal mode)
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [res, setRes] = useState({
    items: [],
    page: { page: 0, size: 25, totalElements: 0, totalPages: 0, hasNext: false, hasPrev: false },
  });

  // Data (search mode)
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchErr, setSearchErr] = useState("");
  const [searchInfo, setSearchInfo] = useState("");
  const [searchItems, setSearchItems] = useState([]);

  const searchSeqRef = useRef(0);
  const suppressAutoFetchRef = useRef(false);

  // Drawer (detail)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState("");
  const [detail, setDetail] = useState(null);

  // Export menu
  const [exportOpen, setExportOpen] = useState(false);

  const inSearchMode = !!safeStr(q);

  // Lock body scroll when drawer is open (NO layout shift)
  useEffect(() => {
    if (!drawerOpen) return;

    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPadRight = body.style.paddingRight;

    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (scrollBarWidth > 0) body.style.paddingRight = `${scrollBarWidth}px`;

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadRight;
    };
  }, [drawerOpen]);

  // Close export on outside click / esc
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        if (exportOpen) setExportOpen(false);
        else if (drawerOpen) setDrawerOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, exportOpen]);

  useEffect(() => {
    function onClick() {
      if (exportOpen) setExportOpen(false);
    }
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [exportOpen]);

  async function fetchList({
    nextPage = page,
    nextSize = size,
    nextQ = q,
    nextBookingStatus = bookingStatus,
  } = {}) {
    if (!enabled) return;

    setLoading(true);
    setErr("");

    try {
      const api = await searchAdminSupportBookingsForUser({
        userId: entityId,
        q: (nextQ || "").trim() || undefined,
        status: nextBookingStatus === "ALL" ? undefined : nextBookingStatus,
        page: nextPage,
        size: nextSize,
      });

      const x = unwrapApi(api);
      const items = Array.isArray(x?.items) ? x.items : Array.isArray(x?.content) ? x.content : [];
      const meta =
        x?.page || {
          page: nextPage,
          size: nextSize,
          totalElements: items.length,
          totalPages: items.length < nextSize ? nextPage + 1 : nextPage + 2,
          hasNext: items.length >= nextSize,
          hasPrev: nextPage > 0,
        };

      setRes({ items, page: meta });
    } catch (e) {
      setErr(e?.message || "Failed to load bookings");
      setRes({
        items: [],
        page: { page: 0, size: nextSize, totalElements: 0, totalPages: 0, hasNext: false, hasPrev: false },
      });
    } finally {
      setLoading(false);
    }
  }

  async function runSearchDataset({ query = q, nextBookingStatus = bookingStatus } = {}) {
    if (!enabled) return;

    const seq = ++searchSeqRef.current;
    const qq = safeStr(query);

    setSearchLoading(true);
    setSearchErr("");
    setSearchInfo("");
    setSearchItems([]);

    try {
      if (!qq) {
        setSearchItems([]);
        setSearchInfo("");
        return;
      }

      const status = nextBookingStatus === "ALL" ? undefined : nextBookingStatus;

      const HARD_MAX_PAGES = 30;
      const HARD_MAX_ITEMS = 2500;
      const SCAN_PAGE_SIZE = 150;

      // Step 1: server-side search
      let acc = [];
      let api0 = await searchAdminSupportBookingsForUser({
        userId: entityId,
        q: qq,
        status,
        page: 0,
        size: SCAN_PAGE_SIZE,
      });

      if (seq !== searchSeqRef.current) return;

      const { items: items0, page: page0, hasNext: hasNext0, totalPages: totalPages0 } = extractListItems(api0);
      acc = acc.concat(items0);

      const serverFoundSomething = acc.length > 0;

      if (serverFoundSomething) {
        let p = 1;
        let hasNext = typeof page0?.hasNext === "boolean" ? page0.hasNext : hasNext0;
        let tp = Number(page0?.totalPages ?? totalPages0 ?? 0) || 0;

        const maxP = tp > 0 ? Math.min(tp, HARD_MAX_PAGES) : HARD_MAX_PAGES;

        while (hasNext && p < maxP && acc.length < HARD_MAX_ITEMS) {
          const apiN = await searchAdminSupportBookingsForUser({
            userId: entityId,
            q: qq,
            status,
            page: p,
            size: SCAN_PAGE_SIZE,
          });

          if (seq !== searchSeqRef.current) return;

          const { items: itemsN, page: pageN } = extractListItems(apiN);
          acc = acc.concat(itemsN);

          hasNext = typeof pageN?.hasNext === "boolean" ? pageN.hasNext : itemsN.length >= SCAN_PAGE_SIZE;
          p += 1;
        }

        const deduped = dedupeByBookingId(acc);
        setSearchItems(deduped);
        setSearchInfo("server");
        return;
      }

      // Step 2: fallback scan WITHOUT q
      acc = [];
      let p = 0;
      let hasNext = true;

      while (hasNext && p < HARD_MAX_PAGES && acc.length < HARD_MAX_ITEMS) {
        const apiN = await searchAdminSupportBookingsForUser({
          userId: entityId,
          status,
          page: p,
          size: SCAN_PAGE_SIZE,
        });

        if (seq !== searchSeqRef.current) return;

        const { items: itemsN, page: pageN } = extractListItems(apiN);
        acc = acc.concat(itemsN);

        hasNext = typeof pageN?.hasNext === "boolean" ? pageN.hasNext : itemsN.length >= SCAN_PAGE_SIZE;
        p += 1;
      }

      const deduped = dedupeByBookingId(acc);
      setSearchItems(deduped);
      setSearchInfo("scan");
    } catch (e) {
      setSearchErr(e?.message || "Search failed");
      setSearchItems([]);
      setSearchInfo("");
    } finally {
      setSearchLoading(false);
    }
  }

  async function openDetail(bookingId) {
    if (bookingId == null) return;

    setDrawerOpen(true);
    setActiveId(bookingId);
    setDetail(null);
    setDetailErr("");
    setDetailLoading(true);

    try {
      const data = await getAdminSupportBookingDetail(bookingId, { auditPage: 0, auditSize: 50 });
      setDetail(data);
    } catch (e) {
      setDetailErr(e?.message || "Failed to load booking detail");
    } finally {
      setDetailLoading(false);
    }
  }

  // On entity change: reset view + single fetch
  useEffect(() => {
    if (!enabled) return;

    suppressAutoFetchRef.current = true;
    setQ("");
    setBookingStatus("ALL");
    setRideStatus("ALL");
    setPage(0);
    setSize(25);
    setSearchItems([]);
    setSearchErr("");
    setSearchInfo("");

    fetchList({ nextPage: 0, nextQ: "", nextBookingStatus: "ALL", nextSize: 25 }).finally(() => {
      suppressAutoFetchRef.current = false;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, entityId]);

  // When filters/paging change (not q), fetch (ONLY when not searching)
  useEffect(() => {
    if (!enabled) return;
    if (suppressAutoFetchRef.current) return;
    if (inSearchMode) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, page, size, bookingStatus]);

  // Debounced q search
  useEffect(() => {
    if (!enabled) return;

    const t = setTimeout(() => {
      const nextQ = safeStr(q);

      if (!nextQ) {
        setSearchItems([]);
        setSearchErr("");
        setSearchInfo("");
        if (page !== 0) setPage(0);
        else fetchList({ nextPage: 0, nextQ: "", nextBookingStatus: bookingStatus, nextSize: size });
        return;
      }

      if (page !== 0) setPage(0);
      runSearchDataset({ query: nextQ, nextBookingStatus: bookingStatus });
    }, 350);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // If booking status changes during search, rerun
  useEffect(() => {
    if (!enabled) return;
    if (!inSearchMode) return;
    setPage(0);
    runSearchDataset({ query: q, nextBookingStatus: bookingStatus });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingStatus]);

  const listLoading = inSearchMode ? searchLoading : loading;
  const listErr = inSearchMode ? searchErr : err;

  const baseRows = inSearchMode ? searchItems : res.items || [];
  const serverMeta = res.page || {};

  // Client-side filter for q + ride status
  const filteredRows = useMemo(() => {
    const rsFilter = up(rideStatus);
    const qs = safeStr(q);

    return (baseRows || [])
      .filter((r) => rowMatchesQuery(r, qs))
      .filter((r) => {
        const rRide = rideFromRow(r);
        const rRideId = rRide?.rideId ?? null;
        const rRideStatus = rideStatusFromRow(r);

        if (rsFilter === "ALL") return true;
        if (rsFilter === "NO_RIDE") return rRideId == null;
        return rRideStatus === rsFilter;
      });
  }, [baseRows, rideStatus, q]);

  // Pagination (server vs search)
  const ui = useMemo(() => {
    if (inSearchMode) {
      const totalElements = filteredRows.length;
      const safeSize = Math.max(1, Number(size || 25));
      const totalPages = Math.max(1, Math.ceil(totalElements / safeSize));
      const safePage = Math.min(Math.max(0, Number(page || 0)), totalPages - 1);
      return {
        mode: "search",
        page: safePage,
        size: safeSize,
        totalElements,
        totalPages,
        hasPrev: safePage > 0,
        hasNext: safePage < totalPages - 1,
      };
    }

    const totalElements = Number(serverMeta.totalElements ?? baseRows.length ?? 0);
    const totalPages = Math.max(1, Number(serverMeta.totalPages ?? 1));
    const safePage = Math.min(Math.max(0, Number(page || 0)), totalPages - 1);
    return {
      mode: "server",
      page: safePage,
      size: Math.max(1, Number(serverMeta.size ?? size ?? 25)),
      totalElements,
      totalPages,
      hasPrev: !!serverMeta.hasPrev && safePage > 0,
      hasNext: !!serverMeta.hasNext && safePage < totalPages - 1,
    };
  }, [inSearchMode, filteredRows.length, size, page, serverMeta, baseRows.length]);

  // Keep page in range in search mode
  useEffect(() => {
    if (!inSearchMode) return;
    if (page !== ui.page) setPage(ui.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inSearchMode, ui.page]);

  const fromIdx = ui.totalElements === 0 ? 0 : ui.page * ui.size + 1;
  const toIdx = ui.totalElements === 0 ? 0 : Math.min(ui.totalElements, (ui.page + 1) * ui.size);

  const pageButtons = useMemo(
    () => buildPageButtons({ page: ui.page, totalPages: ui.totalPages, maxButtons: 7 }),
    [ui.page, ui.totalPages]
  );

  const pageRows = useMemo(() => {
    if (inSearchMode) {
      const start = ui.page * ui.size;
      const end = start + ui.size;
      return filteredRows.slice(start, end);
    }
    return filteredRows;
  }, [inSearchMode, filteredRows, ui.page, ui.size]);

  const { booking, auditItems, auditMeta, raw: detailUnwrapped } = useMemo(
    () => extractBookingAndAudit(detail),
    [detail]
  );
  const ride = useMemo(() => extractRideBestEffort({ detailEnvelope: detail }), [detail]);
  const payment = useMemo(() => extractPaymentBestEffort({ detailEnvelope: detail }), [detail]);

  const routeSchedule = useMemo(
    () => extractRouteScheduleBestEffort({ detailEnvelope: detail, booking }),
    [detail, booking]
  );

  const fareBreakdown = useMemo(
    () => computeFareBreakdown({ booking, payment }),
    [booking, payment]
  );

  // ------------------ EXPORT HANDLERS ------------------
  function exportCsv(scope) {
    const now = new Date();
    const stamp =
      String(now.getFullYear()) +
      "-" +
      String(now.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(now.getDate()).padStart(2, "0") +
      "_" +
      String(now.getHours()).padStart(2, "0") +
      String(now.getMinutes()).padStart(2, "0");

    const rows = scope === "page" ? pageRows : filteredRows;
    const csv = rowsToCsv(rows, {
      entityId,
      bookingStatus,
      rideStatus,
      q: safeStr(q),
    });

    const fname =
      `support_bookings_user_${entityId}_` + (safeStr(q) ? "search_" : "") + `${scope}_${stamp}.csv`;

    downloadCsv(csv, fname);
    setExportOpen(false);
  }

  if (!enabled) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="text-sm font-extrabold text-slate-900">Coming soon</div>
        <div className="mt-1 text-[13px] font-medium text-slate-500">
          Bookings support is enabled for <span className="font-semibold text-slate-900">Users</span> in phase-1.
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-4">
      <Section
        title="Bookings"
        subtitle="Read-only booking support (User context). Route & schedule are shown only in drawer."
        right={
          <div className="flex items-center gap-2">
            <ScopeChip label={`#${entityId}`} />
            <Button
              tone="soft"
              disabled={listLoading}
              onClick={() => {
                if (inSearchMode) runSearchDataset({ query: q, nextBookingStatus: bookingStatus });
                else fetchList({ nextPage: ui.page, nextSize: size });
              }}
            >
              <IconRefresh className="h-4 w-4 text-slate-700" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>

            {/* Export dropdown */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <Button
                tone="soft"
                disabled={listLoading || ui.totalElements === 0}
                onClick={() => setExportOpen((v) => !v)}
                title={ui.totalElements === 0 ? "No rows to export" : "Export CSV"}
              >
                <IconDownload className="h-4 w-4 text-slate-700" />
                <span className="hidden sm:inline">Export</span>
                <IconChevronDown className="h-4 w-4 text-slate-500" />
              </Button>

              {exportOpen ? (
                <div className="absolute right-0 top-11 z-20 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                  <button
                    type="button"
                    onClick={() => exportCsv("page")}
                    className="w-full px-4 py-3 text-left text-sm font-semibold text-slate-900 hover:bg-slate-50"
                  >
                    Export page (CSV)
                    <div className="mt-1 text-[12px] font-medium text-slate-500">
                      {pageRows.length} rows currently visible
                    </div>
                  </button>
                  <div className="h-px bg-slate-200" />
                  <button
                    type="button"
                    onClick={() => exportCsv("all")}
                    className="w-full px-4 py-3 text-left text-sm font-semibold text-slate-900 hover:bg-slate-50"
                  >
                    Export all (filtered) CSV
                    <div className="mt-1 text-[12px] font-medium text-slate-500">
                      {filteredRows.length} rows match filters/search
                    </div>
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-center">
            {/* Search */}
            <div className="min-w-0 lg:col-span-7">
              <div className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 focus-within:border-slate-300">
                <IconSearch className="h-4 w-4 shrink-0 text-slate-500" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search: bookingId / rideId / paymentId / statuses / ownerId / driverId / razorpay ids / pickup / destination"
                  className="min-w-0 w-full bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
                />
                {q ? (
                  <button
                    onClick={() => setQ("")}
                    className="shrink-0 rounded-md border border-slate-200 bg-white p-1 text-slate-600 hover:bg-slate-50"
                    title="Clear"
                    type="button"
                  >
                    <IconX className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>

            {/* Booking Status */}
            <div className="min-w-0 lg:col-span-3">
              <SelectField
                value={bookingStatus}
                onChange={(e) => {
                  setBookingStatus(e.target.value);
                  setPage(0);
                }}
                title="Booking status (DB Booking.status)"
              >
                {BOOKING_STATUS_VALUES.map((s) => (
                  <option key={s} value={s}>
                    {s === "ALL" ? "All booking statuses" : s}
                  </option>
                ))}
              </SelectField>
            </div>

            {/* Ride Status */}
            <div className="min-w-0 lg:col-span-2">
              <SelectField value={rideStatus} onChange={(e) => setRideStatus(e.target.value)} title="Ride status">
                {RIDE_STATUS_VALUES.map((s) => (
                  <option key={s} value={s}>
                    {s === "ALL" ? "All ride statuses" : s === "NO_RIDE" ? "No ride" : s}
                  </option>
                ))}
              </SelectField>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[13px] font-medium text-slate-500">
              {listLoading
                ? inSearchMode
                  ? "Searching…"
                  : "Loading…"
                : `${pageRows.length} shown • ${ui.totalElements} total • ${fromIdx}-${toIdx}`}
              {inSearchMode && searchInfo ? (
                <span className="ml-2 text-[12px] font-semibold text-slate-400">
                  {searchInfo === "server" ? "• server search" : "• full scan (payment/razorpay search)"}
                </span>
              ) : null}
            </div>
            <div className="text-[12px] font-medium text-slate-400">
              Tip: Rows are not clickable — use <span className="font-semibold text-slate-600">View</span>.
            </div>
          </div>

          {listErr ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
              {listErr}
            </div>
          ) : null}
        </div>
      </Section>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-3">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Bookings list</div>
        </div>

        {listLoading ? (
          <div className="px-5 py-10 text-sm font-semibold text-slate-600">
            {inSearchMode ? "Searching bookings…" : "Loading bookings…"}
          </div>
        ) : pageRows.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="text-sm font-semibold text-slate-900">No bookings found</div>
            <div className="mt-1 text-[13px] font-medium text-slate-500">Try adjusting filters or search query.</div>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            {/* ✅ CLEAN MAIN TABLE: Route + schedule removed; only shows IDs/status/payment/fare */}
            <table className="w-full min-w-[1240px] table-auto">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[120px]">
                    Booking
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[150px]">
                    Status
                  </th>

                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[120px]">
                    Ride ID
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[160px]">
                    Ride Status
                  </th>

                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[180px]">
                    Vehicle
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[110px]">
                    Owner ID
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[110px]">
                    Driver ID
                  </th>

                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[140px]">
                    Fare (incl GST)
                  </th>

                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[140px]">
                    Payment ID
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[170px]">
                    Payment Status
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[170px]">
                    Created
                  </th>
                  <th className="px-5 py-3 text-right text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[110px]">
                    Open
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200">
                {pageRows.map((r, idx) => {
                  const id = r?.bookingId ?? r?.id ?? r?.booking_id;
                  const key = id != null ? String(id) : `row-${idx}`;

                  const createdAt = fmtDT(r?.createdAt).pretty;

                  const ownerId = r?.owner?.ownerId ?? r?.ownerId ?? r?.owner_id ?? "—";
                  const driverId =
                    r?.assignedDriver?.driverId ??
                    r?.driver?.driverId ??
                    r?.driverId ??
                    r?.driver_id ??
                    "—";

                  const vehicleLabel =
                    r?.vehicle?.registrationNumber ||
                    r?.vehicle?.regNo ||
                    r?.vehicle?.vehicleNo ||
                    r?.vehicleRegNo ||
                    (r?.vehicle?.vehicleId ? `V-${r.vehicle.vehicleId}` : "—");

                  const rideRow = rideFromRow(r);
                  const rideId = rideRow?.rideId ?? null;
                  const rRideStatus = rideStatusFromRow(r) || null;

                  const payRow = paymentFromRow(r);
                  const payId = payRow?.paymentId ?? null;
                  const payStatus = payRow?.paymentStatus ?? "";

                  const bStatus = bookingStatusFromRow(r);

                  const fare = computeFareBreakdown({ row: r, payment: payRow });

                  return (
                    <tr key={key} className="hover:bg-slate-50">
                      <td className="px-5 py-4 align-middle">
                        <div className="whitespace-nowrap tabular-nums text-[13px] font-medium text-slate-900">
                          {id == null ? "—" : `#${id}`}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="min-w-0">
                          <StatusTag status={bStatus} />
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="whitespace-nowrap tabular-nums text-[13px] font-medium text-slate-900">
                          {rideId == null ? "—" : `#${rideId}`}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="min-w-0">
                          {rideId == null ? (
                            <Tag label="NO RIDE" tone="neutral" />
                          ) : rRideStatus ? (
                            <RideStatusTag status={rRideStatus} />
                          ) : (
                            <Tag label="—" tone="neutral" />
                          )}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="min-w-0">
                          <div className="truncate text-[13px] font-medium text-slate-900" title={vehicleLabel || ""}>
                            {vehicleLabel}
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="whitespace-nowrap tabular-nums text-[13px] font-medium text-slate-900">
                          {ownerId}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="whitespace-nowrap tabular-nums text-[13px] font-medium text-slate-900">
                          {driverId}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="whitespace-nowrap tabular-nums text-[13px] font-semibold text-slate-900">
                          {fmtMoney(fare.total)}
                        </div>
                        <div className="mt-1 text-[12px] font-medium text-slate-500">
                          GST: {fare.gst == null ? "—" : fmtMoney(fare.gst)}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="whitespace-nowrap tabular-nums text-[13px] font-medium text-slate-900">
                          {payId == null ? "—" : `#${payId}`}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="min-w-0">
                          {payId == null && !payStatus ? (
                            <Tag label="—" />
                          ) : (
                            <PaymentStatusTag status={payStatus || "—"} />
                          )}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="min-w-0 text-[13px] font-medium text-slate-900 break-words whitespace-normal">
                          {createdAt}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle text-right">
                        <button
                          type="button"
                          onClick={() => openDetail(id)}
                          disabled={id == null}
                          className={classNames(
                            "inline-flex h-10 items-center justify-center gap-2 rounded-md border px-3 text-sm font-semibold",
                            id == null
                              ? "border-slate-200 bg-white text-slate-400 cursor-not-allowed"
                              : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
                          )}
                        >
                          View <IconChevronRight className="h-4 w-4 text-slate-500" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ✅ Pagination Footer — Ops standard style */}
        <div className="px-5 py-3 border-t border-slate-200 bg-white">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-xs text-slate-500">
              Showing{" "}
              <span className="font-semibold tabular-nums text-slate-700">
                {fromIdx}-{toIdx}
              </span>{" "}
              of <span className="font-semibold tabular-nums text-slate-700">{ui.totalElements}</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {/* View select */}
              <div className="flex items-center gap-2">
                <div className="text-xs text-slate-500">View</div>
                <div className="relative">
                  <select
                    value={String(size)}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setSize(next);
                      setPage(0);
                    }}
                    className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    title="Rows per page"
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">▼</span>
                </div>
              </div>

              {/* Page label */}
              <div className="text-sm text-slate-700">
                Page <span className="font-semibold tabular-nums">{ui.page + 1}</span> of{" "}
                <span className="font-semibold tabular-nums">{ui.totalPages}</span>
              </div>

              {/* Prev / Next */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={listLoading || !ui.hasPrev}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                >
                  Prev
                </button>

                <button
                  type="button"
                  disabled={listLoading || !ui.hasNext}
                  onClick={() => setPage((p) => p + 1)}
                  className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                  title={!ui.hasNext ? "No more results" : ""}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          {ui.totalPages > 1 ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {pageButtons.map((x, i) => {
                if (x === "…") {
                  return (
                    <span key={`dots-${i}`} className="px-1 text-[13px] font-extrabold text-slate-400">
                      …
                    </span>
                  );
                }
                const p = Number(x);
                return (
                  <PageButton
                    key={`p-${p}`}
                    active={p === ui.page}
                    disabled={listLoading}
                    label={String(p + 1)}
                    onClick={() => setPage(p)}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      {/* Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[55]">
          <div className="absolute inset-0 bg-black/25" onClick={() => setDrawerOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-3xl overflow-y-auto border-l border-slate-200 bg-white shadow-2xl">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">
                    Booking support detail
                  </div>
                  <div className="mt-1 truncate text-2xl font-extrabold tracking-tight text-slate-900">
                    Booking <span className="tabular-nums">#{activeId}</span>
                  </div>

                  {booking ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <StatusTag status={normalizeBookingStatus(booking.status)} />
                      {ride?.rideId ? <Tag label={`RIDE ${ride.rideId}`} /> : null}
                      {ride?.rideStatus ? <RideStatusTag status={normalizeRideStatus(ride.rideStatus)} /> : null}
                      {payment?.paymentId ? <Tag label={`PAY ${payment.paymentId}`} /> : null}
                      {payment?.paymentStatus ? <PaymentStatusTag status={payment.paymentStatus} /> : null}
                    </div>
                  ) : null}

                  <div className="mt-3 text-[13px] font-medium text-slate-500">
                    Support is <span className="font-semibold text-slate-700">read-only</span>. For any operational
                    changes, use the Operations page.
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={() => setDrawerOpen(false)} tone="soft">
                    Close
                  </Button>
                </div>
              </div>

              {detailErr ? (
                <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
                  {detailErr}
                </div>
              ) : null}
            </div>

            <div className="space-y-4 px-5 py-4">
              {detailLoading ? (
                <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm font-semibold text-slate-600">
                  Loading detail…
                </div>
              ) : !booking ? (
                <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm font-semibold text-slate-600">
                  No detail loaded.
                </div>
              ) : (
                <>
                  {/* ✅ Route + Schedule ONLY IN DRAWER */}
                  <Section title="Route & schedule" subtitle="Pickup/drop locations, stops, and times.">
                    {(() => {
                      const rs = routeSchedule || {};
                      const pickupAt = fmtDT(rs.pickupAt);
                      const dropAt = fmtDT(rs.dropAt);

                      const stops = Array.isArray(rs.stops) ? rs.stops : [];
                      const hasStops = stops.length > 0;

                      return (
                        <>
                          <KV k="Pickup location" v={rs.pickupText || "—"} />
                          <KV k="Drop location" v={rs.destinationText || "—"} />

                          <KV
                            k="Stops"
                            v={
                              hasStops ? (
                                <div className="space-y-2">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Tag label={`${stops.length} STOP${stops.length === 1 ? "" : "S"}`} tone="warn" />
                                  </div>
                                  <div className="mt-1 space-y-1">
                                    {stops.map((s, i) => (
                                      <div key={`${i}-${s}`} className="text-[13px] font-medium text-slate-700">
                                        {i + 1}. {s}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-[13px] font-medium text-slate-500">No stops</span>
                              )
                            }
                          />

                          <div className="my-2 h-px bg-slate-200" />
                          <KV k="Pickup time" v={pickupAt.pretty} hint={pickupAt.rel ? `(${pickupAt.rel})` : ""} />
                          <KV k="Drop time" v={dropAt.pretty} hint={dropAt.rel ? `(${dropAt.rel})` : ""} />
                        </>
                      );
                    })()}
                  </Section>

                  {/* Booking summary */}
                  <Section title="Booking summary" subtitle="Support-focused identifiers and timestamps.">
                    {(() => {
                      const created = fmtDT(booking.createdAt);
                      const updated = fmtDT(booking.updatedAt);

                      const vehicleLabel =
                        booking?.vehicle?.registrationNumber ||
                        booking?.vehicle?.regNo ||
                        booking?.vehicle?.vehicleNo ||
                        (booking?.vehicle?.vehicleId ? `V-${booking.vehicle.vehicleId}` : "—");

                      const userId = booking?.user?.userId ?? entityId ?? "—";
                      const ownerId = booking?.owner?.ownerId ?? "—";
                      const driverId = booking?.assignedDriver?.driverId ?? "—";

                      return (
                        <>
                          <KV
                            k="Status"
                            v={
                              <div className="flex flex-wrap items-center gap-2">
                                <StatusTag status={normalizeBookingStatus(booking.status)} />
                              </div>
                            }
                          />

                          <KV
                            k="User"
                            v={<span className="tabular-nums">{userId !== "—" ? `#${userId}` : "—"}</span>}
                            action={
                              userId !== "—" ? (
                                <button
                                  type="button"
                                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] font-semibold text-slate-900 hover:bg-slate-50"
                                  onClick={() => copyText(userId)}
                                >
                                  Copy
                                </button>
                              ) : null
                            }
                          />

                          <KV k="Vehicle" v={vehicleLabel} />
                          <KV k="Owner ID" v={ownerId} />
                          <KV k="Driver ID" v={driverId} />

                          <div className="my-2 h-px bg-slate-200" />
                          <KV k="Created at" v={created.pretty} hint={created.rel ? `(${created.rel})` : ""} />
                          <KV k="Updated at" v={updated.pretty} hint={updated.rel ? `(${updated.rel})` : ""} />

                          {/* Payment summary */}
                          <div className="my-2 h-px bg-slate-200" />
                          <KV
                            k="Payment ID"
                            v={payment?.paymentId != null ? `#${payment.paymentId}` : "—"}
                            action={
                              payment?.paymentId != null ? (
                                <button
                                  type="button"
                                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] font-semibold text-slate-900 hover:bg-slate-50"
                                  onClick={() => copyText(payment.paymentId)}
                                >
                                  Copy
                                </button>
                              ) : null
                            }
                          />
                          <KV
                            k="Payment Status"
                            v={payment?.paymentStatus ? <PaymentStatusTag status={payment.paymentStatus} /> : "—"}
                          />
                          {payment?.razorpayOrderId ? <KV k="Razorpay Order" v={payment.razorpayOrderId} /> : null}
                          {payment?.razorpayPaymentId ? <KV k="Razorpay Payment" v={payment.razorpayPaymentId} /> : null}
                          {payment?.razorpayRefundId ? <KV k="Razorpay Refund" v={payment.razorpayRefundId} /> : null}
                        </>
                      );
                    })()}
                  </Section>

                  {/* Fare breakdown */}
                  <Section
                    title="Fare breakdown"
                    subtitle="Backend-calculated pricing, wallet deduction, and driver net."
                  >
                    <KV k="Total fare" v={fmtMoney(fareBreakdown.total)} />
                    <KV k="Platform fee" v={fmtMoney(fareBreakdown.platformFee)} />
                    <KV k="GST" v={fmtMoney(fareBreakdown.gst)} />
                    <KV k="Wallet deduction" v={fmtMoney(fareBreakdown.walletDeduction)} />
                    <KV k="Net to driver" v={fmtMoney(fareBreakdown.netAmountToDriver)} />
                  </Section>

                  {/* Ride details */}
                  <Section
                    title="Ride details"
                    subtitle={
                      ride?.rideId
                        ? "Read-only for support clarity. Use Operations page for live handling."
                        : "No ride linked (or not returned by backend)."
                    }
                  >
                    {!ride ? (
                      <div className="text-sm font-medium text-slate-500">No ride object available.</div>
                    ) : (
                      <>
                        <KV
                          k="Ride"
                          v={
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="tabular-nums text-sm font-extrabold text-slate-900">
                                {ride.rideId != null ? `#${ride.rideId}` : "—"}
                              </span>
                              {ride.rideStatus ? (
                                <RideStatusTag status={normalizeRideStatus(ride.rideStatus)} />
                              ) : (
                                <Tag label="—" tone="neutral" />
                              )}
                            </div>
                          }
                        />
                        <KV k="Arrived at" v={fmtDT(ride.arrivedAt).pretty} />
                        <KV k="Start time" v={fmtDT(ride.startTime).pretty} />
                        <KV k="End time" v={fmtDT(ride.endTime).pretty} />

                        <div className="my-2 h-px bg-slate-200" />
                        <KV k="Total distance (km)" v={fmtNum(ride.totalDistanceKm, 3)} />
                        <KV k="Planned distance (km)" v={fmtNum(ride.plannedDistanceKm, 3)} />
                        <KV k="ETA" v={ride.eta ?? "—"} />
                        <KV k="Planned end time" v={fmtDT(ride.plannedEndTime).pretty} />

                        <div className="my-2 h-px bg-slate-200" />
                        <KV k="Current location" v={fmtLatLng(ride.currentLatitude, ride.currentLongitude)} />
                        <KV
                          k="Current destination"
                          v={fmtLatLng(ride.currentDestinationLatitude, ride.currentDestinationLongitude)}
                        />
                      </>
                    )}
                  </Section>

                  {/* Audit timeline */}
                  <Section
                    title="Audit timeline"
                    subtitle={
                      auditMeta
                        ? `Most recent first • page ${Number(auditMeta.page ?? 0) + 1} • ${auditItems.length} items`
                        : "Most recent audit entries first."
                    }
                  >
                    <details className="group rounded-xl border border-slate-200 bg-slate-50">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3">
                        <div className="min-w-0">
                          <div className="text-sm font-extrabold text-slate-900">
                            Timeline ({auditItems.length})
                          </div>
                          <div className="mt-1 text-[12px] font-medium text-slate-500">
                            Click to{" "}
                            <span className="group-open:hidden">expand</span>
                            <span className="hidden group-open:inline">collapse</span>
                          </div>
                        </div>
                        <span className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] font-semibold text-slate-700">
                          <span className="group-open:hidden">Open</span>
                          <span className="hidden group-open:inline">Close</span>
                        </span>
                      </summary>

                      <div className="border-t border-slate-200 px-4 py-4">
                        {auditItems.length === 0 ? (
                          <div className="text-sm font-medium text-slate-500">No audit entries for this booking.</div>
                        ) : (
                          <div className="space-y-2">
                            {auditItems.slice(0, 25).map((a, idx) => (
                              <div
                                key={String(a?.id ?? `${a?.createdAt ?? "x"}-${idx}`)}
                                className="rounded-xl border border-slate-200 bg-white p-4"
                              >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="text-sm font-extrabold text-slate-900">
                                    {a.actionKey || a.action || "AUDIT"}
                                    {a.actorId ? <span className="tabular-nums text-slate-500"> #{a.actorId}</span> : null}
                                  </div>
                                  <div className="text-[12px] font-medium text-slate-500">
                                    {fmtDT(a.createdAt).pretty}{" "}
                                    {fmtDT(a.createdAt).rel ? `(${fmtDT(a.createdAt).rel})` : ""}
                                  </div>
                                </div>

                                {a.note ? <div className="mt-2 text-[13px] font-medium text-slate-700">{a.note}</div> : null}

                                {a.payloadJson ? (
                                  <details className="mt-3">
                                    <summary className="cursor-pointer text-[12px] font-semibold text-slate-700">Payload</summary>
                                    <pre className="mt-2 max-h-[220px] overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] font-medium text-slate-700">
                                      {String(a.payloadJson)}
                                    </pre>
                                  </details>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </details>
                  </Section>

                  {/* Raw payload */}
                  <div className="rounded-xl border border-slate-200 bg-white">
                    <details className="group">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-5 py-4">
                        <div>
                          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Raw payload</div>
                          <div className="mt-1 text-[13px] font-medium text-slate-500">Expand only when needed.</div>
                        </div>
                        <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[12px] font-semibold text-slate-700 group-open:hidden">
                          Show
                        </span>
                        <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[12px] font-semibold text-slate-700 hidden group-open:inline">
                          Hide
                        </span>
                      </summary>
                      <div className="border-t border-slate-200 px-5 py-4">
                        <pre className="max-h-[320px] overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] font-medium text-slate-700">
                          {JSON.stringify(detailUnwrapped ?? detail, null, 2)}
                        </pre>
                      </div>
                    </details>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
